// Side-effecting tab opener for the find-references results tab.
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 24.
//
// Mirrors `openDataProfileTab` shape: looks up an existing references tab
// for the same (connection, database, identifier, kind) and re-uses it
// (mirrors the "click again on the same column shouldn't pile up tabs" UX
// expected by users), otherwise creates a new one. The database is part of
// the match — a tab caches its database for body fetches and click-through —
// taken on BOTH sides through `resolvedDatabaseFor` first, so a request that
// names no database and one that spells the login default out (`''` and
// `master` on a connection the dialog left blank) land on the one tab they
// already share an index with, and then compared with
// `databaseComparisonKey` (`connection-lifecycle/databaseIdentity.ts`),
// which is the REQUEST's identity, not the index's: exact everywhere except a
// SQL Server instance whose probed collation says `Sales` and `sales` are one
// database. Two cases are deliberately stricter than the index's own key: a
// MySQL / Oracle / SQLite connection holds one index for every schema, but a
// bare identifier still resolves against the database the request names; and
// while a SQL Server instance's collation is UNKNOWN, "same database?" has no
// answer yet. A second tab in either case is harmless; reusing the wrong one
// is not — it would show one database's groups under another's header,
// routing and cached bodies.
//
// The IDENTIFIER itself is compared the same way the index would GROUP it:
// via `IdentifierIndex.objectKeyCaseFor`, the frozen per-(database, object
// kind) case policy every other keyed surface in this tree already reads
// (index building, DDL invalidation, find-references / rename grouping). A
// folded comparison here would let a `dbo.v` search fill and re-title a
// `dbo.V` tab on a catalog that tells the two apart. Only MySQL's answer
// varies by object kind, so this takes the most conservative component across
// every kind — a component counts as case-sensitive if ANY kind keeps it that
// way — and falls back to comparing every component exactly while the target
// database's index has not been built yet (policy unknown): an extra tab is
// the safe direction, a folded reuse is not.
//
// The tab opens in a `loading` state and is filled asynchronously: the
// identifier index is built per (connection, database) and DEFERRED — behind
// the schema snapshot and an idle moment for the connection's configured
// database, and not at all for any other database until someone asks. So the
// opener awaits `indexReadyFor(connectionId, database)`, which creates and
// builds the target database's index on demand, and only then reads it.
// Before 2026-09-12 it read the single per-connection index synchronously,
// which on a multi-database SQL Server connection reported "No references
// found" for every table outside the configured database — and for every table
// at all when the tab opened before the deferred build had run.
//
// A rejected `indexReadyFor` carries a typed REASON
// (`connection-lifecycle/navIndexUnavailable.ts`), and the reason — not the
// connection's status — decides what the tab shows. `no-lifecycle` means this
// connection has no index and never will right now (a mock connection, an
// engine the app does not index, a connection that left the store): nothing
// was searched because there is nothing to search, so the honest empty result
// stands. `policy-unknown`, `cancelled` and `build-failed` mean the search did
// not happen on a connection the user can still see, so the tab says the index is unavailable
// rather than settling into the same "No references found" this whole change
// exists to remove. Either way, a connection that has gone away since keeps
// the empty result: there is nothing to retry.

import type { DatabaseEngineId } from '@/core/engines';

import { getConnectionById } from '../../connections/utils/connectionLookup';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import {
  isReferencesTab,
  type ReferencesTab,
  type Tab,
} from '../../workspace/core/types/workspaceTypes';

import { databaseEngineIdToSqlEngine } from '../../intellisense/versionGating/engineIdMapping';
import { databaseComparisonKey } from '../connection-lifecycle/databaseIdentity';
import { isNavIndexUnavailable } from '../connection-lifecycle/navIndexUnavailable';
import {
  getIdentifierIndexFor,
  indexReadyFor,
  resolvedDatabaseFor,
} from '../connection-lifecycle/navigationalLifecycle';
import { objectKeyFor } from '../index/identifierIndex';
import { getMysqlLowerCaseTableNamesFor } from '../mysqlCaseProbe';

import { findReferences } from './findReferencesService';
import type {
  ReferencesTabContent,
} from './referencesTabTypes';
import type { IdentifierKind, ObjectKeyCase, ObjectKind, QualifiedName } from '../types';
import { trackNavFeature, type NavTrigger } from '../analytics/trackNavFeature';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

export type OpenReferencesTabOptions = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly identifier: QualifiedName;
  readonly identifierKind: IdentifierKind;
  /** Source object that owns the identifier (column's host table/view). Used
   *  to label the tab — references tabs without a relation still work. */
  readonly relation?: QualifiedName;
  /** How the user triggered the find-references action. Defaults to
   * 'keybinding' when not supplied (Monaco Shift+F12 path). */
  readonly trigger?: NavTrigger;
};

export type OpenReferencesTabResult = {
  readonly opened: boolean;
  readonly tabId?: string;
  /** Settles once the tab holds its results — an empty `ready` state when the
   *  connection has no index at all (mock / disconnected), an `error` state
   *  when a still-live connection's index could not be built. Never rejects. */
  readonly ready: Promise<void>;
};

export function openReferencesTab(
  opts: OpenReferencesTabOptions,
): OpenReferencesTabResult {
  const { tabs, addTab, setActiveTab, updateTabContent } =
    useWorkspaceStore.getState();
  const { activeConnection, connection } = getConnectionById(opts.connectionId);
  const connectionName = safeConnectionAlias(connection, 'Unnamed Connection');
  const serverHost = safeConnectionServer(activeConnection?.connection);

  trackNavFeature({
    name: 'feature:navfeatures_find_references',
    props: { kind: opts.identifierKind, trigger: opts.trigger ?? 'keybinding' },
  });

  // ONE database name for the whole open — the one the lifecycle keys this
  // request under (`resolvedDatabaseFor`). A request that names NO database
  // (a query tab on a connection the dialog left blank) and one that spells the
  // login default out (`master`, from the Object Explorer) share a single
  // index, so they must share a single tab; carrying the resolved name into the
  // content also makes the tab header, the empty state and the body fetches
  // name the real database instead of `''`.
  const request: OpenReferencesTabOptions = {
    ...opts,
    database: resolvedDatabaseFor(opts.connectionId, opts.database),
  };

  // Computed once — it depends only on `request`, not on the candidate tab —
  // rather than once per open tab inside `isMatchingReferencesTab` below.
  const identityPolicy = identityCasePolicy(request.connectionId, request.database);
  const existing = tabs.find((tab) => isMatchingReferencesTab(tab, request, identityPolicy));
  let tabId: string;
  if (existing) {
    setActiveTab(existing.id);
    updateTabContent<'references-tab'>(existing.id, {
      groups: [],
      partial: undefined,
      status: 'loading',
      error: undefined,
    });
    tabId = existing.id;
  } else {
    const content: ReferencesTabContent = {
      connectionId: request.connectionId,
      database: request.database,
      engineId: request.engineId,
      identifier: request.identifier,
      identifierKind: request.identifierKind,
      relation: request.relation,
      groups: [],
      bodies: {},
      status: 'loading',
    };
    tabId = addTab({
      type: 'references-tab',
      title: formatTabTitle(request),
      connectionInfo: {
        connectionId: request.connectionId,
        connectionName,
        serverHost,
        database: request.database,
      },
      content,
    });
  }

  return { opened: true, tabId, ready: fillReferences(request, tabId) };
}

/** Shown when the target database's index could not be built at all. Plain,
 *  and distinct from "No references found" — the two mean opposite things. */
const INDEX_UNAVAILABLE = 'The index for this database could not be built — try again.';

/** Which fill owns a tab's content right now, by token.
 *
 *  A REUSED tab can have two fills in flight at once: the user presses
 *  Shift+F12 again after a disconnect/reconnect while the first wait is still
 *  pending on the cancelled build. Both end in `updateTabContent`, so whichever
 *  settled LAST won — and the older, cancelled one would overwrite the retry's
 *  fresh groups with the error state. Each fill takes the next token from a
 *  counter that never repeats (so a token from a fill that outlived its tab
 *  cannot collide with a later tab's) and writes only while it is still the
 *  tab's latest.
 *
 *  The entry is dropped by the fill that writes, so the map holds only tabs
 *  with a fill in flight — no tab-close hook to keep in sync. */
let fillSequence = 0;
const currentFill = new Map<string, number>();

function beginFill(tabId: string): number {
  fillSequence += 1;
  currentFill.set(tabId, fillSequence);
  return fillSequence;
}

/** Wait for the target database's index, then write the grouped references
 *  into the tab. `'no-lifecycle'` — a mock connection, an engine the app does
 *  not index, a connection that left the store — answers from no index, which
 *  is an honest empty result. The other reasons mean the search never ran, so
 *  on a connection that is still live the tab shows the error state instead:
 *  reporting "no references" there is the false negative this area exists to
 *  avoid. An untyped rejection is a bug rather than an answer and is treated
 *  the same way. */
async function fillReferences(opts: OpenReferencesTabOptions, tabId: string): Promise<void> {
  const token = beginFill(tabId);
  const failure = await indexReadyFor(opts.connectionId, opts.database).then(
    () => undefined,
    (err: unknown) => (isNavIndexUnavailable(err) ? err.reason : 'policy-unknown'),
  );
  // A NEWER fill for this same (reused) tab started while this one waited: it
  // owns the content now, and this completion — error or groups — is stale.
  // Nothing below awaits again, so this ONE check covers both writes.
  if (currentFill.get(tabId) !== token) return;
  currentFill.delete(tabId);
  if (failure !== undefined && failure !== 'no-lifecycle' && isStillConnected(opts.connectionId)) {
    useWorkspaceStore.getState().updateTabContent<'references-tab'>(tabId, {
      groups: [],
      partial: undefined,
      status: 'error',
      error: INDEX_UNAVAILABLE,
    });
    return;
  }
  const result = findReferences(
    opts.connectionId,
    opts.identifier,
    opts.identifierKind,
    {
      database: opts.database,
      engine: databaseEngineIdToSqlEngine(opts.engineId),
      mysqlLowerCaseTableNames: getMysqlLowerCaseTableNamesFor(opts.connectionId, opts.engineId),
    },
  );
  useWorkspaceStore.getState().updateTabContent<'references-tab'>(tabId, {
    groups: result.groups,
    partial: result.partial,
    status: 'ready',
    // A reused tab may carry a previous run's error.
    error: undefined,
  });
}

/** Re-read AFTER the wait: the connection may have dropped while the index was
 *  being built, which is what makes an empty result honest rather than a
 *  failure. */
function isStillConnected(connectionId: string): boolean {
  return getConnectionById(connectionId).activeConnection?.status === 'connected';
}

function isMatchingReferencesTab(
  tab: Tab,
  opts: OpenReferencesTabOptions,
  identityPolicy: ObjectKeyCase,
): tab is ReferencesTab {
  if (!isReferencesTab(tab)) return false;
  if (tab.content.connectionId !== opts.connectionId) return false;
  // Both sides resolved first: a tab opened before the connection learnt its
  // login default still holds `''`, and the request that follows holds
  // `master` — one database, so one tab. `resolvedDatabaseFor` is the identity
  // on a name the request SPELLS, so this is a no-op for every other case.
  const identity = (database: string): string =>
    databaseComparisonKey(
      opts.connectionId,
      opts.engineId,
      resolvedDatabaseFor(opts.connectionId, database),
    );
  if (identity(tab.content.database) !== identity(opts.database)) return false;
  if (tab.content.identifierKind !== opts.identifierKind) return false;
  return identifiersEqual(tab.content.identifier, opts.identifier, identityPolicy);
}

/** Every `ObjectKind` the index can key an object under (`types.ts`) — walked
 *  below to take the most conservative case policy across kinds. Not a
 *  runtime lookup of the union, so keep this in sync if `ObjectKind` grows. */
const ALL_OBJECT_KINDS: readonly ObjectKind[] = [
  'view',
  'materialized-view',
  'inline-tvf',
  'scalar-function',
  'procedure',
  'trigger',
  'default-constraint',
  'check-constraint',
];

/** `objectKeyFor` needs an `ObjectKind` only to namespace its output string;
 *  every comparison below passes this SAME constant on both sides, so it never
 *  affects equality. It exists purely so this module reuses the index's own
 *  per-component case folding (`index/identifierIndex.ts`) instead of a
 *  private one. */
const IDENTITY_KEY_KIND: ObjectKind = 'view';

/** Conservative default while the target database's case policy is unknown
 *  (no index handle yet): compare every component as spelled. It can only
 *  make the opener show one extra tab, never route one target's results into
 *  another target's tab — see the module comment. */
const EXACT_IDENTITY: ObjectKeyCase = { schema: true, name: true };

/** How tab reuse compares identifier spellings for `(connectionId, database)`
 *  — see the module comment. `getIdentifierIndexFor` is synchronous and
 *  returns `null` before that database's index has been built (or ever will
 *  be — a mock connection, an engine this feature does not index), in which
 *  case {@link EXACT_IDENTITY} applies. Every engine but MySQL answers the
 *  SAME `ObjectKeyCase` for every object kind, so any one kind would do; MySQL
 *  varies by kind (routine names fold, trigger names don't), so this takes the
 *  most conservative component across every kind instead — a component counts
 *  as case-sensitive if ANY kind keeps it that way. */
function identityCasePolicy(connectionId: string, database: string): ObjectKeyCase {
  const index = getIdentifierIndexFor(connectionId, database);
  if (!index) return EXACT_IDENTITY;
  let schema = false;
  let name = false;
  for (const kind of ALL_OBJECT_KINDS) {
    const kindPolicy = index.objectKeyCaseFor(kind);
    if (kindPolicy.schema) schema = true;
    if (kindPolicy.name) name = true;
  }
  return { schema, name };
}

function identifiersEqual(a: QualifiedName, b: QualifiedName, policy: ObjectKeyCase): boolean {
  return objectKeyFor(IDENTITY_KEY_KIND, a, policy) === objectKeyFor(IDENTITY_KEY_KIND, b, policy);
}

function formatTabTitle(opts: OpenReferencesTabOptions): string {
  const id = opts.identifier;
  const display = id.schema && id.name ? `${id.schema}.${id.name}` : id.name;
  const relationLabel = opts.relation
    ? ` on ${opts.relation.schema ? `${opts.relation.schema}.${opts.relation.name}` : opts.relation.name}`
    : '';
  return `References: ${display}${relationLabel}`;
}
